import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { Layout } from "@/components/layout";
import { OrganizationInfoCard } from "@/components/organizations/OrganizationInfoCard";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, ShoppingCart, TrendingUp, Users, Zap } from "lucide-react";
import { StepIndicator } from "@/components/onboarding/StepIndicator";

export default function OnboardingWelcome() {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();

  const handleGetStarted = () => {
    setLocation("/pc-onboarding/invite");
  };

  return (
    <Layout hideSidebar>
      <div className="max-w-5xl mx-auto">
        <StepIndicator currentStep={1} totalSteps={3} />
        <div className="text-center mb-12 space-y-4">
          <h1 className="text-4xl font-bold">{t("pages.pcOnboarding.welcome.welcomeToOnsemblePro")}</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {t("pages.pcOnboarding.welcome.builtForYourBusiness")}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Value Proposition Card */}
          <Card className="border-2">
            <CardContent className="pt-6">
              <div className="space-y-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Zap className="h-6 w-6 text-primary" />
                  </div>
                  <h2 className="text-2xl font-semibold">
                    {t("pages.pcOnboarding.welcome.streamlineOrdering")}
                  </h2>
                </div>

                <div className="space-y-4 text-muted-foreground">
                  <p className="leading-relaxed">
                    {t("pages.pcOnboarding.welcome.onsembleProMakesIt")}
                  </p>

                  <div className="space-y-3 pt-2">
                    <div className="flex items-start gap-3">
                      <ShoppingCart className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-foreground">
                          {t("pages.pcOnboarding.welcome.selfServiceOrdering")}
                        </p>
                        <p className="text-sm">
                          {t("pages.pcOnboarding.welcome.contractorsCanBrowseProducts")}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <TrendingUp className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-foreground">
                          {t("pages.pcOnboarding.welcome.betterContractorRetention")}
                        </p>
                        <p className="text-sm">
                          {t("pages.pcOnboarding.welcome.byMakingOrderingEffortless")}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Users className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-foreground">
                          {t("pages.pcOnboarding.welcome.reducedAdministrativeBurden")}
                        </p>
                        <p className="text-sm">
                          {t("pages.pcOnboarding.welcome.automateOrderProcessingAnd")}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Your Profit Center Card */}
          <div>
            <OrganizationInfoCard />
          </div>
        </div>

        {/* Call to Action */}
        <Card className="bg-primary/5 border-2 border-primary/20">
          <CardContent className="pt-6">
            <div className="text-center space-y-6">
              <div className="space-y-2">
                <h2 className="text-2xl font-semibold">
                  {t("pages.pcOnboarding.welcome.readyToGetStarted")}
                </h2>
                <p className="text-muted-foreground max-w-xl mx-auto">
                  {t("pages.pcOnboarding.welcome.inviteYourContractorsTo")}
                </p>
              </div>

              <Button
                size="lg"
                onClick={handleGetStarted}
                className="min-w-48 h-12 text-lg"
              >
                {t("pages.pcOnboarding.welcome.startInvitingContractors")}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
